/**
 * Service de paiement - À remplacer par une implémentation réelle
 * Ce fichier simule le comportement de l'API backend pour le développement
 */

import axios from 'axios';

/**
 * Simule l'initialisation d'un paiement et retourne les informations de redirection CMI
 * @param {Object} paymentData - Données du paiement (licenceId, lang)
 * @returns {Promise<Object>} - La réponse simulée de l'API contenant les informations de redirection CMI
 */
export const initializePayment = async (paymentData) => {
  try {
    console.log('Simulation de initializePayment avec:', paymentData);
    
    const { licenceId, lang = 'fr' } = paymentData;
    
    if (!licenceId) {
      console.error('licenceId est obligatoire pour initialiser un paiement');
      throw new Error('licenceId est obligatoire');
    }

    const secureRandomNum = () => {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array); 
      return array[0] % 1000;
    };
    
    // Génération d'identifiants simulés
    const orderId = `ORD-${Date.now()}-${secureRandomNum()}`;
    const timestamp = new Date().toISOString();
    
    // Simulation d'une réponse réseau
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Structure exacte de la réponse API
    const response = {
      timestamp: timestamp,
      status: 200,
      message: 'Paiement initialisé avec succès',
      body: {
        oid: orderId,
        amount: '1000.00',
        currency: 'MAD',
        clientId: 'TEST_CLIENT',
        billToName: 'Utilisateur Test',
        billToStreet1: '123 Rue de Test',
        billToCity: 'Casablanca',
        billToStateProv: 'Casablanca',
        billToPostalCode: '20000',
        billToCountry: 'MA',
        billToTelVoice: '+212600000000',
        billToEmail: 'test@example.com',
        callbackURL: 'https://portail-fabricant.example.com/callback',
        storeName: 'Portail Fabricant',
        storeType: '3D_PAY_HOSTING',
        tranType: 'PreAuth',
        shopUrl: 'https://portail-fabricant.example.com',
        encoding: 'UTF-8',
        refreshtime: '5',
        lang: lang,
        rnd: `${Date.now()}`,
        hash: 'HASH_SIMULATION',
        hashAlgorithm: 'ver3',
        itemNumber1: licenceId,
        productCode1: 'LICENCE',
        productName1: 'Licence Fabricant',
        qty1: '1',
        itemAmount1: '1000.00'
      }
    };
    
    console.log('Simulation de réponse API complétée:', response);
    return response;
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du paiement:', error);
    throw error;
  }
};

/**
 * Configuration du service pour s'adapter à l'environnement
 */
export const configurePaymentService = () => {
  console.log('Configuration du service de paiement');
  
  // Intercepteur pour adapter les requêtes axios à l'environnement
  axios.interceptors.request.use(
    async (config) => {
      // Si c'est une requête d'initialisation de paiement et nous sommes en dev/test
      if (config.url.includes('/api/paiement/cmi/initialize')) {
        console.log('Intercepting payment initialization request', config);
        
        // Simuler le comportement de l'API
        try {
          const response = await initializePayment(config.data);
          console.log('Réponse simulée générée:', response);
          
          // Rejeter la promesse avec un objet qui imite la réponse axios
          // pour être intercepté par le gestionnaire d'erreur
          return Promise.reject({
            isSimulatedResponse: true,
            response: {
              status: 200,
              data: response
            }
          });
        } catch (error) {
          console.error('Erreur lors de la simulation de la réponse:', error);
          return Promise.reject({
            isSimulatedResponse: true,
            response: {
              status: 500,
              data: { error: "Erreur de simulation" }
            }
          });
        }
      }
      return config;
    }
  );

  // Intercepteur pour les réponses
  axios.interceptors.response.use(
    response => response,
    error => {
      // Si c'est une réponse simulée, renvoyer la réponse comme si elle venait du serveur
      if (error.isSimulatedResponse) {
        console.log('Intercepteur de réponse - Réponse simulée:', error.response);
        return Promise.resolve(error.response);
      }
      return Promise.reject(error);
    }
  );
}; 